import { Box, Slider, Stack, Typography } from '@mui/material';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { UnitProviderProps } from '/@/views/visit-reports/edit/components/field-observation-section/components/precipitation-graphic';

const EnvironmentSliderV2 = ({
    percentiles,
    min,
    max,
    value,
    onChange,
    valueLabelDisplay = 'auto',
    unitProviderProps,
    disabled,
}: {
    percentiles: [number, number, number, number, number];
    min: number;
    max: number;
    value: number;
    onChange: (e: Event, value: number) => void;
    valueLabelDisplay?: 'auto' | 'on';
    unitProviderProps?: UnitProviderProps;
    disabled?: boolean;
}) => {
    const units = useUnitProvider(unitProviderProps);

    const COLOR_PALETTE = {
        VeryLow: { color: '#F5F5F5', label: _('Muy bajo') },
        Medium: { color: '#FFC103', label: _('Medio') },
        High: { color: '#9AD15E', label: _('Alto') },
        VeryHigh: { color: '#006A28', label: _('Muy alto') },
    };

    // Create gradient stops based on percentiles
    const createGradientStops = () => {
        if (percentiles.length !== 5) {
            return `linear-gradient(to right, ${COLOR_PALETTE.VeryLow.color}, ${COLOR_PALETTE.VeryLow.color})`;
        }

        const [p0, p1, p2, p3, p4] = percentiles;
        const totalRange = max - min;

        // Calculate relative positions (0 to 1)
        const pos0 = (p0 - min) / totalRange;
        const pos1 = (p1 - min) / totalRange;
        const pos2 = (p2 - min) / totalRange;
        const pos3 = (p3 - min) / totalRange;
        const pos4 = (p4 - min) / totalRange;

        return `linear-gradient(to right, 
            ${COLOR_PALETTE.VeryLow.color} 0%, 
            ${COLOR_PALETTE.VeryLow.color} ${pos0 * 100}%, 
            ${COLOR_PALETTE.Medium.color} ${pos0 * 100}%, 
            ${COLOR_PALETTE.High.color} ${pos1 * 100}%, 
            ${COLOR_PALETTE.VeryHigh.color} ${pos2 * 100}%, 
            ${COLOR_PALETTE.High.color} ${pos3 * 100}%, 
            ${COLOR_PALETTE.Medium.color} ${pos4 * 100}%, 
            ${COLOR_PALETTE.VeryLow.color} ${pos4 * 100}%, 
            ${COLOR_PALETTE.VeryLow.color} 100%
        )`;
    };

    return (
        <Stack>
            <Stack direction='row' spacing={2} alignItems={'center'}>
                <Typography variant='caption' color='primary.mui.inactive'>
                    {units.toCornYield(min, 2).localeValue}
                </Typography>
                <Slider
                    value={value}
                    onChange={onChange}
                    min={min}
                    max={max}
                    step={100}
                    disabled={disabled}
                    track={false}
                    sx={{
                        '& .MuiSlider-rail': {
                            background: createGradientStops(),
                            height: 4,
                            borderRadius: 4,
                            opacity: 1,
                        },
                    }}
                    valueLabelDisplay={valueLabelDisplay}
                    valueLabelFormat={(value) => {
                        return units.toCornYield(value, 2).toString();
                    }}
                />
                <Typography variant='caption' color='primary.mui.inactive'>
                    {units.toCornYield(max, 2).localeValue}
                </Typography>
            </Stack>
            <Stack direction='row' spacing={1}>
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Probabilidad de ocurrencia')}
                </Typography>
                {Object.values(COLOR_PALETTE).map((item) => (
                    <Stack direction='row' spacing={0.5} key={item.label} alignItems={'center'}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {item.label}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};

export default EnvironmentSliderV2;
